import {
  getGetApiUtilitiesCategoriesIdInsightsQueryOptions,
  useGetApiUtilitiesConfig,
} from '@smarthome/api';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Expand, TrendingUp } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import { z } from 'zod';

import type { ConfigFormProps, RendererProps, WidgetDefinition } from '../types';
import { useDashboardTimeRange } from '../viewer/DashboardTimeRangeContext';

import { HistogramChart, Modal, type HistogramDataset } from '@/components/ui';

/**
 * Time preset options for utility widgets
 */
type UtilityPreset =
  | 'today'
  | 'yesterday'
  | '7d'
  | '30d'
  | 'mtd'
  | 'ytd'
  | 'lastWeek'
  | 'lastMonth';

type BucketSize = '1h' | '1d' | '1w' | '1M';
type ValueMode = 'consumption' | 'cost';

const PRESET_OPTIONS: { value: UtilityPreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'mtd', label: 'Month to date' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'lastMonth', label: 'Last month' },
];

const PRESET_LABELS: Record<UtilityPreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': '7d',
  '30d': '30d',
  mtd: 'MTD',
  ytd: 'YTD',
  lastWeek: 'Last week',
  lastMonth: 'Last month',
};

const BUCKET_OPTIONS: { value: BucketSize; label: string }[] = [
  { value: '1h', label: '1 hour' },
  { value: '1d', label: '1 day' },
  { value: '1w', label: '1 week' },
  { value: '1M', label: '1 month' },
];

/**
 * Map dashboard time range to nearest utility preset
 */
function dashboardRangeToPreset(range: string): UtilityPreset {
  if (range === '7d') return '7d';
  if (range === '30d') return '30d';
  return 'today';
}

/**
 * Utilities Histogram widget config schema
 */
export const utilitiesHistogramConfigSchema = z.object({
  categoryId: z.string().min(1, 'A category is required'),
  valueMode: z.enum(['consumption', 'cost']).default('consumption'),
  bucket: z.enum(['1h', '1d', '1w', '1M']).default('1d'),
  timeRangeMode: z.enum(['dashboard', 'fixed']).default('fixed'),
  preset: z
    .enum(['today', 'yesterday', '7d', '30d', 'mtd', 'ytd', 'lastWeek', 'lastMonth'])
    .default('30d'),
  /** Whether to display per-sensor series as stacked bars */
  stackSensors: z.boolean().default(false),
  /** Show a total stat overlay at the top */
  showTotal: z.boolean().default(true),
});

export type UtilitiesHistogramConfig = z.infer<typeof utilitiesHistogramConfigSchema>;

/**
 * Single category selector
 */
function CategorySelect({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (id: string) => void;
  error?: string;
}) {
  const { data: configData, isLoading } = useGetApiUtilitiesConfig();
  const categories = configData?.config?.categories ?? [];

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        Category
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-lg border px-3 py-2.5 text-sm dark:bg-zinc-900 dark:text-white ${
          error
            ? 'border-red-300 bg-red-50/50 dark:border-red-500/30'
            : 'border-gray-200 bg-white dark:border-white/[0.08]'
        }`}
      >
        <option value="">{isLoading ? 'Loading...' : 'Select a category...'}</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.id ?? ''}>
            {cat.name ?? cat.id}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Config form for Utilities Histogram widget
 */
function UtilitiesHistogramConfigForm({
  config,
  onChange,
  errors,
}: ConfigFormProps<UtilitiesHistogramConfig>) {
  return (
    <div className="space-y-4">
      <CategorySelect
        value={config.categoryId}
        onChange={(id) => onChange({ ...config, categoryId: id })}
        error={errors?.['categoryId']}
      />

      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Show Values
        </label>
        <select
          value={config.valueMode}
          onChange={(e) => onChange({ ...config, valueMode: e.target.value as ValueMode })}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
        >
          <option value="consumption">Consumption</option>
          <option value="cost">Cost</option>
        </select>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Bucket Size
        </label>
        <select
          value={config.bucket}
          onChange={(e) => onChange({ ...config, bucket: e.target.value as BucketSize })}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
        >
          {BUCKET_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Time Range
        </label>
        <select
          value={config.timeRangeMode}
          onChange={(e) =>
            onChange({ ...config, timeRangeMode: e.target.value as 'dashboard' | 'fixed' })
          }
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
        >
          <option value="dashboard">Use dashboard timeframe</option>
          <option value="fixed">Fixed timeframe</option>
        </select>
      </div>

      {config.timeRangeMode === 'fixed' && (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Period
          </label>
          <select
            value={config.preset}
            onChange={(e) => onChange({ ...config, preset: e.target.value as UtilityPreset })}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
          >
            {PRESET_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="space-y-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.showTotal ?? true}
            onChange={(e) => onChange({ ...config, showTotal: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-white/[0.12] dark:bg-zinc-900"
          />
          <span className="text-sm text-gray-700 dark:text-zinc-300">Show period total</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.stackSensors ?? false}
            onChange={(e) => onChange({ ...config, stackSensors: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-white/[0.12] dark:bg-zinc-900"
          />
          <span className="text-sm text-gray-700 dark:text-zinc-300">
            Stack per-sensor breakdown
          </span>
        </label>
      </div>
    </div>
  );
}

/**
 * Format a monetary value with currency code
 */
function formatCost(value: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currencyCode}`;
  }
}

/**
 * Renderer for Utilities Histogram widget
 */
const UtilitiesHistogramRenderer = memo(function UtilitiesHistogramRenderer({
  config,
}: RendererProps<UtilitiesHistogramConfig>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const dashboardTimeRange = useDashboardTimeRange();

  const effectivePreset: string =
    config.timeRangeMode === 'dashboard'
      ? dashboardRangeToPreset(dashboardTimeRange)
      : config.preset;

  const { data, isLoading, isError } = useQuery(
    getGetApiUtilitiesCategoriesIdInsightsQueryOptions(
      config.categoryId,
      { preset: effectivePreset, bucket: config.bucket },
      { query: { enabled: !!config.categoryId, refetchInterval: 60000 } }
    )
  );

  const presetLabel =
    config.timeRangeMode === 'dashboard'
      ? dashboardTimeRange
      : (PRESET_LABELS[config.preset as UtilityPreset] ?? config.preset);

  const currencyCode = data?.currencyCode ?? 'EUR';
  const categoryName = data?.category?.name ?? '';
  const unitSymbol = data?.category?.unitSymbol ?? '';

  // Build chart data from series
  const { singleData, stackedDatasets, useStacked } = useMemo(() => {
    const series = data?.series ?? [];
    const sensorSeries = data?.sensorSeries ?? [];
    const shouldStack = (config.stackSensors ?? false) && sensorSeries.length > 1;

    if (shouldStack) {
      const datasets: HistogramDataset[] = sensorSeries.map((ss) => ({
        label: ss.sensorName ?? ss.sensorHumanId ?? '',
        data: (ss.data ?? []).map((pt) => ({
          timestamp: pt.timestamp ?? '',
          value: config.valueMode === 'cost' ? (pt.cost ?? null) : (pt.consumption ?? null),
        })),
      }));
      return { singleData: [], stackedDatasets: datasets, useStacked: true };
    }

    const single = series.map((pt) => ({
      timestamp: pt.timestamp ?? '',
      value: config.valueMode === 'cost' ? (pt.cost ?? null) : (pt.consumption ?? null),
    }));
    return { singleData: single, stackedDatasets: [], useStacked: false };
  }, [data?.series, data?.sensorSeries, config.valueMode, config.stackSensors]);

  // Total for the period
  const total = useMemo(() => {
    const totals = data?.totals;
    if (!totals) return null;
    return config.valueMode === 'cost' ? totals.cost : totals.consumption;
  }, [data?.totals, config.valueMode]);

  // Derive start/end from range response, fallback to 30-day window
  const { startTime, endTime } = useMemo(() => {
    const range = data?.range;
    if (range?.start && range?.end) {
      return { startTime: range.start, endTime: range.end };
    }
    const now = Date.now();
    return {
      startTime: new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString(),
      endTime: new Date(now).toISOString(),
    };
  }, [data?.range]);

  const chartUnit = config.valueMode === 'cost' ? currencyCode : unitSymbol;

  if (!config.categoryId) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-zinc-500">No category configured</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <AlertTriangle className="mb-2 h-5 w-5 text-amber-500" />
        <p className="text-xs text-gray-500 dark:text-zinc-500">Failed to load utility data</p>
      </div>
    );
  }

  const chartContent = (
    <HistogramChart
      data={singleData}
      datasets={useStacked ? stackedDatasets : undefined}
      stacked={useStacked}
      bucketSize={config.bucket}
      startTime={startTime}
      endTime={endTime}
      unit={chartUnit}
      height={undefined}
      isLoading={isLoading}
    />
  );

  return (
    <>
      <div
        className="group relative flex h-full cursor-pointer flex-col gap-1"
        onClick={() => setIsExpanded(true)}
      >
        {/* Expand hint */}
        <div className="absolute right-2 top-2 z-10 rounded-md bg-black/50 p-1 opacity-0 transition-opacity group-hover:opacity-100">
          <Expand className="h-3.5 w-3.5 text-white" />
        </div>

        {/* Total overlay */}
        {(config.showTotal ?? true) && !isLoading && total !== null && total !== undefined && (
          <div className="flex items-center justify-end px-1">
            <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
              {config.valueMode === 'cost'
                ? formatCost(total, currencyCode)
                : `${total.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unitSymbol}`}
            </span>
          </div>
        )}

        <div className="min-h-0 flex-1">{chartContent}</div>
      </div>

      {/* Fullscreen modal */}
      <Modal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        title={categoryName || 'Utility Histogram'}
        subtitle={`${config.valueMode === 'cost' ? 'Cost' : `Consumption (${unitSymbol})`} · ${presetLabel} · ${config.bucket} buckets`}
        size="xl"
      >
        <div className="h-[60vh]">
          <HistogramChart
            data={singleData}
            datasets={useStacked ? stackedDatasets : undefined}
            stacked={useStacked}
            bucketSize={config.bucket}
            startTime={startTime}
            endTime={endTime}
            unit={chartUnit}
            height={undefined}
            isLoading={isLoading}
          />
        </div>
        {total !== null && total !== undefined && (
          <div className="mt-4 flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
            <span className="text-sm text-gray-500 dark:text-zinc-400">Period total</span>
            <span className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
              {config.valueMode === 'cost'
                ? formatCost(total, currencyCode)
                : `${total.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unitSymbol}`}
            </span>
          </div>
        )}
      </Modal>
    </>
  );
});

/**
 * Utilities Histogram widget definition
 */
export const utilitiesHistogramWidget: WidgetDefinition<UtilitiesHistogramConfig> = {
  type: 'utilities-histogram',
  displayName: 'Utility Histogram',
  description: 'Bar chart of utility consumption or cost over time',
  icon: TrendingUp,
  defaultSize: { w: 4, h: 3 },
  minSize: { w: 3, h: 2 },
  maxSize: { w: 12, h: 6 },
  configSchema: utilitiesHistogramConfigSchema,
  defaultConfig: {
    categoryId: '',
    valueMode: 'consumption',
    bucket: '1d',
    timeRangeMode: 'fixed',
    preset: '30d',
    stackSensors: false,
    showTotal: true,
  },
  ConfigForm: UtilitiesHistogramConfigForm,
  Renderer: UtilitiesHistogramRenderer,
  supportsTimeRange: true,
};
